<template lang="pug">
    AiToolsTabs.chatbot-conversation-list
        el-card.contains-table(shadow="always")
            .error(v-if="error")
                template(v-if="error.code")
                    Error(:message="$t(error.message || 'Something went wrong!')" :code="error.code")
                template(v-else)
                    Error(:message="$t('Something went wrong!')" :code="501")
            .conversations-table(v-else)
                paginated-items(
                    scope="ex"
                    type="chatbot_conversation"
                    :loading="loading"
                    :items="conversationsPaginated.items"
                    :filters="filters"
                    :show-filters="showFilters"
                    :variables="conversationsPaginated.variables"
                    :all-columns="conversationsPaginated.allColumns"
                    :active-columns="conversationsPaginated.activeColumns"
                    :page-info="conversationsPaginated.pageInfo"
                    :sort="sort"
                    @pagination-size-change="handleSizeChange"
                    @pagination-current-change="handleCurrentChange"
                    @show-table-columns="handleColsShow"
                    @change-filter="handleFilterInputChange"
                    @change-sort="handleSortInputChange"
                    @click="handleClick"
                )
                    template(slot="top")
                        el-row.table-top(v-loading="loading" type="flex" justify="end")
                            .right.hide-on-empty
                                el-button(
                                    plain
                                    size="mini"
                                    type="primary"
                                    icon="el-icon-fa-filter fas"
                                    @click="toggleFilterVisibility"
                                    :disabled="disable.showFilters || hasFilters"
                                )
                                    span(v-if="showFilters") {{ $t('Hide filters') }}
                                    span(v-else) {{ $t('Show filters') }}
</template>

<i18n>
{
    "en": {},
    "de": {
        "Show filters": "Filter anzeigen",
        "Hide filters": "Filter verbergen",
        "Conversations could not be fetched.": "Konversationen konnten nicht abgerufen werden.",
        "An error occurred": "Ein Fehler ist aufgetreten"
    }
}
</i18n>

<script>
import _ from 'lodash';
import { adminClient as apolloClient } from '@/store/apollo';

import router from '@/router';
import pinia from '@/pinia';
import { useSetupStore, useChatbotConversationStore } from '@/stores';

import PaginatedItems from '@/components/PaginatedItems';
import Error from '@/components/Error';
import AiToolsTabs from '@/components/AiToolsTabs';

export default {
    name: 'ChatbotConversationList',

    components: {
        PaginatedItems,
        Error,
        AiToolsTabs
    },

    data() {
        return {
            error: false,
            disable: {
                showFilters: false
            }
        };
    },

    computed: {
        showFilters() {
            return this.hasFilters || _.get(this.conversationsPaginated, 'showFilters') || false;
        },
        hasFilters() {
            return !_.isEmpty(this.filters);
        },
        filters() {
            return _.cloneDeep(this.conversationsPaginated.filters);
        },
        activeCms() {
            return useSetupStore().activeCms;
        },
        enabledExtensions() {
            return useSetupStore().enabledExtensions;
        },
        conversationStore() {
            return useChatbotConversationStore();
        },
        loading() {
            return this.conversationStore.loading;
        },
        conversationsPaginated() {
            return this.conversationStore.conversationsPaginated;
        },
        sort() {
            const variables = this.conversationsPaginated.variables;
            const defaultSort = this.conversationsPaginated.defaultSort || { field: 'created_at', direction: 'desc' };
            const order = (variables.order !== undefined && variables.order !== '') ? variables.order : defaultSort.field;
            const direction = (variables.direction !== undefined && variables.direction !== '') ? variables.direction : defaultSort.direction;
            return { field: order, direction: direction };
        },
        setupStore() {
            return useSetupStore();
        }
    },

    watch: {
        activeCms(cms) {
            const reload = _.get(cms, 'reloadComponents', true);
            if (reload) this.initialize();
        }
    },

    methods: {
        initialize() {
            if (!this.enabledExtensions.includes('chatbot')) {
                this.conversationStore.updateLoading(false);
                return;
            }

            this.conversationStore.updateLoading(true);
            this.refetch();
        },
        handleClick(item) {
            router.push({ name: 'chatbot-conversation-detail', params: { id: item.id } });
        },
        handleSizeChange(itemsPerPage) {
            this.conversationStore.updateLoading(true);
            this.conversationStore.defineItemsPerPage(itemsPerPage);
            this.refetch();
        },
        handleCurrentChange(currentPage) {
            this.conversationStore.updateLoading(true);
            this.conversationStore.defineCurrentPage(currentPage);
            this.refetch();
        },
        handleColsShow(visibleColumns) {
            this.conversationStore.defineActiveColumns(visibleColumns);
            apolloClient.clearStore();
        },
        handleFilterInputChange(key, value, debounce = true) {
            this.conversationStore.defineFilterValue({ key, value });
            if (debounce) this.debouncedRefetch();
            else this.refetch();
        },
        handleSortInputChange(key, value) {
            this.conversationStore.defineSorting({ key, value });
            this.refetch();
        },
        toggleFilterVisibility() {
            const visible = this.showFilters;
            const value = !visible;

            this.disable.showFilters = true;
            this.conversationStore.defineFilterVisibility(value);
            this.saveFilterVisibility({ scope: 'ex', key: 'chatbot_conversation', value })
                .finally(() => { this.disable.showFilters = false; });
        },
        refetch() {
            this.conversationStore.fetchConversationsPaginated()
                .then(() => this.conversationStore.updateLoading(false))
                .catch(err => {
                    const { graphQLErrors } = err;

                    let code = 501;
                    let message = 'An error occurred!';

                    if (graphQLErrors) {
                        code = _.get(graphQLErrors, '0.code') || code;
                        message = _.get(graphQLErrors, '0.message') || message;
                    }

                    let notify = true;
                    switch (code) {
                        case 401:
                            notify = false;
                            this.error = { code, message: 'You are not authorized' };
                            break;
                        case 403:
                            notify = false;
                            this.error = { code, message: 'You are not allowed to be here' };
                            break;
                        default:
                            this.error = true;
                            break;
                    }

                    if (notify) {
                        this.$notify.error({
                            title: this.$t(message),
                            message: this.$t('Conversations could not be fetched.'),
                            iconClass: 'fas el-icon-fa-times',
                            position: 'bottom-left',
                            customClass: 'dark',
                            duration: 8000
                        });
                    }

                    this.conversationStore.updateLoading(false);
                    console.error(err);
                });
        },
        debouncedRefetch: _.debounce(function() {
            this.conversationStore.updateLoading(true);
            this.refetch();
        }, useSetupStore(pinia).debounce || 300),
        saveFilterVisibility(payload) {
            return this.setupStore.saveFilterVisibility(payload);
        }
    },

    mounted() {
        this.initialize();
    }
};
</script>

<style lang="sass" scoped>
.error
    padding: 20px
</style>
